import { useState, useEffect, useCallback, useRef } from 'react';

const STORAGE_KEY = 'blockNotificationsEnabled';

const supportsNotifications = () =>
  typeof window !== 'undefined' && 'Notification' in window;

export const useBlockNotifications = (block, isNewBlock, enabledFlag = true) => {
  const [enabled, setEnabled] = useState(() => {
    if (!supportsNotifications()) return false;
    try {
      return localStorage.getItem(STORAGE_KEY) === '1' &&
        Notification.permission === 'granted';
    } catch (e) {
      return false;
    }
  });
  const [permission, setPermission] = useState(() =>
    supportsNotifications() ? Notification.permission : 'unsupported'
  );
  const lastNotifiedHeightRef = useRef(null);

  useEffect(() => {
    if (!enabledFlag) return;
    if (!enabled) return;
    if (!isNewBlock || !block?.height) return;
    if (lastNotifiedHeightRef.current === block.height) return;
    if (permission !== 'granted') return;
    if (document.visibilityState === 'visible') return;

    lastNotifiedHeightRef.current = block.height;

    const feeStr = block.medianFee != null
      ? ` · ${Number(block.medianFee).toFixed(1)} s/vB`
      : '';
    const poolStr = block.pool ? ` · ${block.pool}` : '';

    try {
      const notif = new Notification(`Block ${block.height.toLocaleString()} mined`, {
        body: `${block.txCount?.toLocaleString() || '?'} txs${feeStr}${poolStr}`,
        icon: '/logo192.jpg',
        tag: 'new-block',
        silent: false,
      });
      notif.onclick = () => {
        window.focus();
        notif.close();
      };
    } catch (e) {
      // older browsers may not support options
    }
  }, [block, isNewBlock, enabled, permission, enabledFlag]);

  const [dismissedPrompt, setDismissedPrompt] = useState(false);

  const toggle = useCallback(async () => {
    if (!supportsNotifications()) return;

    const live = Notification.permission;
    setPermission(live);

    if (enabled) {
      setEnabled(false);
      try { localStorage.setItem(STORAGE_KEY, '0'); } catch (e) {}
      return;
    }

    if (live === 'granted') {
      setEnabled(true);
      setDismissedPrompt(false);
      try { localStorage.setItem(STORAGE_KEY, '1'); } catch (e) {}
      return;
    }

    if (live === 'denied') return;

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result === 'granted') {
        setEnabled(true);
        setDismissedPrompt(false);
        try { localStorage.setItem(STORAGE_KEY, '1'); } catch (e) {}
      } else if (result === 'default') {
        setDismissedPrompt(true);
      }
    } catch (e) {
      setPermission(Notification.permission);
    }
  }, [enabled]);

  return {
    enabled,
    permission,
    dismissedPrompt,
    supported: supportsNotifications() && enabledFlag,
    toggle,
  };
};
